/**
 * Page Loading Service
 *
 * Manages page transition loading indicators.
 * Features:
 * - Top loading bar for route transitions
 * - Integration with router events
 * - Minimum display time to prevent flash
 * - NProgress-style behavior
 */

import { createPageLoadingBar } from '../components/spinner';

/**
 * Page loading service options
 */
export interface PageLoadingOptions {
  /** Minimum time to show the loading bar (ms) */
  minimumDisplayTime?: number;
  /** Delay before showing loading bar (ms) to prevent flash for fast loads */
  showDelay?: number;
  /** Parent element to append the loading bar to */
  parent?: HTMLElement;
}

/**
 * Page loading state
 */
interface LoadingState {
  isLoading: boolean;
  element: HTMLElement | null;
  showTimer: number | null;
  hideTimer: number | null;
  startTime: number | null;
}

/**
 * Default options
 */
const DEFAULT_OPTIONS: Required<PageLoadingOptions> = {
  minimumDisplayTime: 300,
  showDelay: 100,
  parent: document.body,
};

/**
 * Global page loading state
 */
const state: LoadingState = {
  isLoading: false,
  element: null,
  showTimer: null,
  hideTimer: null,
  startTime: null,
};

/**
 * Current options
 */
let currentOptions: Required<PageLoadingOptions> = { ...DEFAULT_OPTIONS };

/**
 * Configure the page loading service
 */
export function configurePageLoading(options: PageLoadingOptions): void {
  currentOptions = {
    ...DEFAULT_OPTIONS,
    ...options,
    parent: options.parent || document.body,
  };
}

/**
 * Start the page loading indicator
 */
export function startPageLoading(): void {
  // Clear any pending hide timer
  if (state.hideTimer) {
    clearTimeout(state.hideTimer);
    state.hideTimer = null;
  }

  // If already loading, don't restart
  if (state.isLoading) {
    return;
  }

  state.isLoading = true;

  // Show after delay (to prevent flash for fast navigations)
  state.showTimer = window.setTimeout(() => {
    if (!state.isLoading) return;

    state.startTime = Date.now();

    // Create and append loading bar
    state.element = createPageLoadingBar();
    currentOptions.parent.appendChild(state.element);

    // Animate in
    requestAnimationFrame(() => {
      if (state.element) {
        state.element.style.opacity = '1';
      }
    });
  }, currentOptions.showDelay);
}

/**
 * Stop the page loading indicator
 */
export function stopPageLoading(): void {
  // Clear show timer if not yet shown
  if (state.showTimer) {
    clearTimeout(state.showTimer);
    state.showTimer = null;
  }

  // If not loading or element not shown, just reset state
  if (!state.isLoading || !state.element) {
    state.isLoading = false;
    return;
  }

  // Calculate how long to wait before hiding
  const elapsed = state.startTime ? Date.now() - state.startTime : 0;
  const remainingTime = Math.max(0, currentOptions.minimumDisplayTime - elapsed);

  // Hide after minimum display time
  state.hideTimer = window.setTimeout(() => {
    if (state.element) {
      // Animate out
      state.element.style.opacity = '0';
      state.element.style.transition = 'opacity 0.2s ease-out';

      // Remove after animation
      setTimeout(() => {
        if (state.element) {
          state.element.remove();
          state.element = null;
        }
        state.isLoading = false;
        state.startTime = null;
      }, 200);
    } else {
      state.isLoading = false;
      state.startTime = null;
    }
  }, remainingTime);
}

/**
 * Check if page is currently loading
 */
export function isPageLoading(): boolean {
  return state.isLoading;
}

/**
 * Force complete the loading (skip minimum display time)
 */
export function forceStopPageLoading(): void {
  if (state.showTimer) {
    clearTimeout(state.showTimer);
    state.showTimer = null;
  }

  if (state.hideTimer) {
    clearTimeout(state.hideTimer);
    state.hideTimer = null;
  }

  if (state.element) {
    state.element.remove();
    state.element = null;
  }

  state.isLoading = false;
  state.startTime = null;
}

/**
 * Higher-order function to wrap async operations with page loading
 */
export function withPageLoading<T>(asyncFn: () => Promise<T>): () => Promise<T> {
  return async () => {
    startPageLoading();
    try {
      return await asyncFn();
    } finally {
      stopPageLoading();
    }
  };
}

/**
 * Class-based page loading controller
 */
export class PageLoadingController {
  private options: Required<PageLoadingOptions>;

  constructor(options: PageLoadingOptions = {}) {
    this.options = {
      ...DEFAULT_OPTIONS,
      ...options,
      parent: options.parent || document.body,
    };
  }

  /**
   * Start loading
   */
  start(): void {
    configurePageLoading(this.options);
    startPageLoading();
  }

  /**
   * Stop loading
   */
  stop(): void {
    stopPageLoading();
  }

  /**
   * Force stop loading
   */
  forceStop(): void {
    forceStopPageLoading();
  }

  /**
   * Check if loading
   */
  isLoading(): boolean {
    return isPageLoading();
  }

  /**
   * Wrap an async function with loading indicator
   */
  wrap<T>(asyncFn: () => Promise<T>): () => Promise<T> {
    return async () => {
      this.start();
      try {
        return await asyncFn();
      } finally {
        this.stop();
      }
    };
  }
}

export default PageLoadingController;
